import type { FC } from 'react'
import React, { useRef } from 'react'
import { FormLabel } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles'
import Select from 'react-select'
import ReactMultiSelectCheckboxes from 'react-multiselect-checkboxes'
import clsx from 'clsx'

const ArrowDown = () => (
  <svg width={11} height={5} xmlns="http://www.w3.org/2000/svg">
    <g fill="none" fillRule="evenodd">
      <path d="M-6-9h23v23H-6z" />
      <path
        d="M1 0l4.5 4.5L10 0"
        stroke="#000433"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
)

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      color: '#000433',
      fontSize: '14px',
      letterSpacing: '-0.23px',
      lineHeight: '22px',
      fontWeight: 500,
      '& .customSelect__single-value': {
        color: theme.colors.grey550,
      },
      '& .customSelect__placeholder': {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: theme.colors.grey450,
        width: '85%',
      },
      '& .customSelect__clear-indicator': {
        paddingRight: 0,
        paddingLeft: 0,
        marginRight: '-5px',
      },
      '& .customSelect__menu': {
        backgroundColor: theme.colors.white,
        zIndex: 2,
      },
      '& .customSelect__indicators': {
        padding: '0 8px',
        '& svg': {
          marginLeft: 10,
        },
      },
      '& .customSelect__multi-value__label': {
        color: '#fff',
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: '-0.17px',
        lineHeight: '15px',
        paddingRight: 6,
        paddingLeft: 0,
      },
      '& .customSelect__multi-value': {
        backgroundColor: '#000433',
        color: '#00DEB6',
        flexDirection: 'row-reverse',
        flexWrap: 'nowrap',
        borderRadius: `${2}px!important`,
        overflow: 'hidden',
        gap: '8px',
      },
      '& .customSelect__multi-value__remove': {
        backgroundColor: `#000433 !important`,
        color: `#fff !important`,
      },
      '& .customSelect__control--is-disabled': {
        backgroundColor: '#f7f6f6!important',
      },
    },
    transparent: {
      backgroundColor: 'transparent',
      '& .customSelect__control, & .customSelect__control:focus': {
        boxShadow: 'none',
      },
    },
    wrapper: {},
    checkboxes: {
      '& button': {
        width: '100%',
        border: '1px solid #4E516B',
        borderRadius: '5px',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 3px 0 rgba(46,81,123,0.04)',
        position: 'relative',
        minHeight: 38,
        zIndex: 10,
        fontFamily: 'Effra, sans-serif!important',
        fontWeight: 500,
        color: theme.colors.grey450,
        '&:after': {
          content: '""',
          backgroundImage: 'url("assets/icons/medium/arrow-down.svg")',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 12,
          position: 'absolute',
          top: 16,
          right: 8,
          width: 14,
          height: 11,
        },
        '& svg': {
          display: 'none',
        },
        '& ~ div': {
          zIndex: '100!important',
          '& ~ div': {
            zIndex: '99!important',
          },
        },
      },
      '& .customSelect__option': {
        position: 'relative',
        backgroundColor: theme.colors.white,
        color: '#000433',
        fontWeight: 400,
        '&:after': {
          content: '""',
          backgroundImage: 'url("assets/icons/check_box_outline.svg")',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 17,
          position: 'absolute',
          top: 10,
          left: 8,
          width: 17,
          height: 17,
        },
        '&:hover': {
          cursor: 'pointer',
          backgroundColor: '#f5f5f5 !important',
        },
      },
      '& .customSelect__option--is-selected': {
        '&:after': {
          backgroundImage: 'url("assets/icons/check_box.svg")!important',
        },
      },
      '& .customSelect__value-container': {
        color: theme.colors.grey550,
      },
      '& input': {
        opacity: 0,
      },
    },
  }),
)

const customStyles = (config: any) => ({
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    color: '#000433 !important',
  }),
  control: (provided) => ({
    ...provided,
    border: '1px solid #4E516B',
    borderRadius: '5px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0 1px 3px 0 rgba(46,81,123,0.04)',
    flexWrap: 'nowrap',
  }),
  menu: (base) => ({
    ...base,
    width: 'max-content',
    minWidth: '100%',
    ...(config?.alignRight && {
      position: 'absolute',
      right: 0,
    }),
  }),
})

interface IOption {
  label: string
  value: string
}

interface IProps {
  value?: string | string[]
  options?: IOption[]
  customVariant?: string
  placeholder?: string
  label?: string
  isMulti?: boolean
  isSearchable?: boolean
  withCheckboxes?: boolean
  alignRight?: boolean
}

const CustomSelect: FC<IProps> = ({
  value,
  options,
  customVariant,
  label,
  isMulti = false,
  isSearchable = false,
  withCheckboxes = false,
  placeholder,
  alignRight,
  ...rest
}) => {
  const selectWrapperRef = useRef<HTMLDivElement>(null)
  const classes = useStyles()
  const theme = useTheme()

  const selectTheme = (defaultTheme) => ({
    ...defaultTheme,
    borderRadius: 0,
    fontWeight: 500,
    colors: {
      ...defaultTheme.colors,
      primary: theme.colors.primary,
      primary25: '#F5F5F5',
      primary75: theme.colors.primary,
    },
  })

  const resolveValue = () => {
    try {
      if (isMulti) {
        const multiOptions = options.filter((option: IOption) => {
          return value.includes(option?.value)
        })
        if (!multiOptions || !multiOptions.length) return []
        return multiOptions
      }
      return options.find((option: IOption) => option?.value === value)
    } catch {
      return isMulti ? [] : null
    }
  }

  return (
    <div className={classes.wrapper} ref={selectWrapperRef}>
      {!!label && <FormLabel>{label}</FormLabel>}
      {withCheckboxes ? (
        <div className={classes.checkboxes}>
          <ReactMultiSelectCheckboxes
            styles={{
              ...customStyles({ alignRight }),
            }}
            className={clsx(
              classes.root,
              customVariant === 'transparent' && classes.transparent,
            )}
            classNamePrefix="customSelect"
            theme={selectTheme}
            options={options}
            menuPlacement="auto"
            isSearchable
            isMulti={isMulti}
            {...rest}
            value={resolveValue()}
            placeholderButtonLabel={placeholder}
            // menuIsOpen={false}
          />
        </div>
      ) : (
        <Select
          styles={{
            ...customStyles({ alignRight }),
          }}
          className={clsx(
            classes.root,
            'c-select',
            customVariant === 'transparent' && classes.transparent,
          )}
          components={{
            DropdownIndicator: ArrowDown,
          }}
          classNamePrefix="customSelect"
          theme={selectTheme}
          options={options}
          value={resolveValue() || ''}
          menuPlacement="auto"
          isSearchable={isSearchable}
          isMulti={isMulti}
          {...rest}
          placeholder={placeholder}
        />
      )}
    </div>
  )
}

export default CustomSelect as typeof Select
